import styled from '@emotion/styled';
import { InlineAlert, theme } from '@travelnest/hui';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../api';
import { IPropertyListings } from '../../../data/entities/listing/listing';
import { IProperty } from '../../../data/entities/property';
import { useCustomerExperiments } from '../../../experiment';

const Container = styled.div`
	margin-bottom: ${theme.spacing.space50};
`;

interface IPropertyWithListings {
	id: IProperty['id'];
	name: IProperty['name'];
	listings: IPropertyListings[];
}

export const CustomerAlerts: React.FC<{
	properties: IProperty[];
	customerId?: string;
}> = ({ properties, customerId = '' }) => {
	const navigate = useNavigate();
	const [propertiesWithIssues, setPropertiesWithIssues] = useState<IPropertyWithListings[]>([]);
	const customerExperiments = useCustomerExperiments(customerId);
	const shouldRenderPropertyIssuesAlerts = customerExperiments?.new_listings_experience === 'on';

	const fetchListingsForAllProperties = async (propertiesToFetch: IProperty[]) => {
		const propertiesWithListings = await Promise.all(propertiesToFetch.map((async ({ id, name }) => {
			const listings = await api.listingsV2.getListingsByPropertyId(id);
			return { id, name, listings };
		})));
		// filter all properties with at least one listing with an issue
		setPropertiesWithIssues(propertiesWithListings.filter(
			({ listings }) => listings.some(
				(listing) => listing?.listingIssues && listing?.listingIssues?.length > 0
			)
		));
	};

	useEffect(() => {
		if (shouldRenderPropertyIssuesAlerts) {
			fetchListingsForAllProperties(properties);
		}
	}, [properties, shouldRenderPropertyIssuesAlerts]);

	const propertiesAlertsToShow = propertiesWithIssues?.map(({ id, name }) => (
		<Container key={id}>
			<InlineAlert
				variant="negative"
				heading="Action required: Additional information needed"
				content={`${name} requires additional information before it can go live on one or more channels.`}
				actionButton={{ text: 'View details', onClick: () => navigate(`/property/${id}/listings?experience=new`) }}
			/>
		</Container>
	));

	const alertsToShow  = [...propertiesAlertsToShow];

	return (
		(shouldRenderPropertyIssuesAlerts && propertiesWithIssues?.length > 0) ? (
			<>
				{alertsToShow}
			</>
		) : null
	);
};
